import React, { useState } from 'react';

interface FeatureCard {
  title: string;
  description: string;
  icon?: string;
}

const WhatYouCanGet: React.FC = () => {
  const [expandedItems, setExpandedItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setExpandedItems(prev => 
      prev.includes(index) 
        ? prev.filter(item => item !== index)
        : [...prev, index]
    );
  };
  const features: FeatureCard[] = [
    {
      title: "Curated Stock Picks",
      description: "Selected junior mining stocks with breakout potential that we are investing in."
    },
    {
      title: "Real-Time Insights",
      description: "Drill Bits videos and instant commentary on company announcements, powered by Robert’s  expertise as an industry insider."
    },
    {
      title: "Deep-Dive Education",
      description: "Teaching you how to spot winner and avoid duds."
    },
    {
      title: "10x Tracker",
      description: "Hunt stocks with 10x potential, analyzing past successes like Filo Mining, Great Bear, and Snowline Gold."
    },
    {
      title: "Live Engagement",
      description: "Real-time Q&A and exclusive interviews with industry leaders."
    },
    {
      title: "Community Power",
      description: "Connect with ambitious investors to share ideas and grow together."
    }
  ];

  return (
    <section className="mx-auto px-4 md:px-10 lg:px-32 w-full bg-[#F6F6F6] pt-20 pb-20">
      <div className="">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h2 className="text-[30px] md:text-[40px] lg:text-[32px] font-[700] leading-[40px] text-[#000] mb-7">
          What You Get with The Wealthy Miner
          </h2>
        </div>

        {/* Features Grid - Desktop Only */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-[10px] p-8"
            >
              <div className="mb-3">
                <div className="w-16 h-16 flex items-center justify-left mb-4">
                  {index === 0 ? (
                    <img 
                      src="/images/stock.webp" 
                      alt="Stock icon" 
                      className="w-10 h-10 object-contain"
                    />
                  ) : index === 1 ? (
                    <img 
                      src="/images/time.webp" 
                      alt="Time icon" 
                      className="w-10 h-10 object-contain"
                    />
                  ) : index === 2 ? (
                    <img 
                      src="/images/deep.webp" 
                      alt="Deep analysis icon" 
                      className="w-10 h-10 object-contain"
                    />
                  ) : index === 3 ? (
                    <img 
                      src="/images/deep.webp" 
                      alt="Deep analysis icon" 
                      className="w-10 h-10 object-contain"
                    />
                  ) : index === 4 ? (
                    <img 
                      src="/images/live.webp" 
                      alt="Live updates icon" 
                      className="w-10 h-10 object-contain"
                    />
                  ) : index === 5 ? (
                    <img 
                      src="/images/community.webp" 
                      alt="Community icon" 
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <span className="text-white text-2xl font-bold">
                      {index + 1}
                    </span>
                  )}
                </div>
                <h3 className="text-[24px] font-[600] text-[#000]">
                  {feature.title}
                </h3>
              </div>
              <p className="text-[18px] text-[#000] leading-[1.6]">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile Dropdown Layout */}
        <div className="block sm:hidden space-y-4">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-[#D9D9D9] rounded-[10px] p-4"
            >
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => toggleItem(index)}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    {index === 0 ? (
                      <img 
                        src="/images/stock.webp" 
                        alt="Stock icon" 
                        className="w-10 h-10 object-contain"
                      />
                    ) : index === 1 ? (
                      <img 
                        src="/images/time.webp" 
                        alt="Time icon" 
                        className="w-10 h-10 object-contain"
                      />
                    ) : index === 2 ? (
                      <img 
                        src="/images/deep.webp" 
                        alt="Deep analysis icon" 
                        className="w-10 h-10 object-contain"
                      />
                    ) : index === 3 ? (
                      <img 
                        src="/images/deep.webp" 
                        alt="Deep analysis icon" 
                        className="w-10 h-10 object-contain"
                      />
                    ) : index === 4 ? (
                      <img 
                        src="/images/live.webp" 
                        alt="Live updates icon" 
                        className="w-10 h-10 object-contain"
                      />
                    ) : index === 5 ? (
                      <img 
                        src="/images/community.webp" 
                        alt="Community icon" 
                        className="w-10 h-10 object-contain"
                      />
                    ) : null}
                  </div>
                  <h3 className="text-[18px] font-[600] text-[#000]">
                    {feature.title}
                  </h3>
                </div>
                <svg 
                  className={`w-5 h-5 text-[#000] transition-transform duration-200 ${
                    expandedItems.includes(index) ? 'rotate-180' : ''
                  }`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              
              {expandedItems.includes(index) && (
                <div className="mt-4 pt-4 border-t border-[#F6F6F6]">
                  <p className="text-[14px] text-[#000] leading-[1.6]">
                    {feature.description}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-16 text-[12px] lg:text-[18px] md:text-[15px]">
          <a href="https://the-wealthy-miner.mn.co/" target="_blank" rel="noopener noreferrer" className="inline-block w-full sm:w-auto bg-[#0B6054] text-white lg:px-10 py-3.5 rounded-[10px] font-semibold hover:bg-[#1A2A44] transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#1A2A44] focus:ring-opacity-50 text-center">
            JOIN A TRIBE OF WINNING SPECULATORS
          </a>
        </div>
      </div>
    </section>
  );
};

export default WhatYouCanGet;
